import { create } from 'zustand';

export type ProcessingStatus = 'idle' | 'processing' | 'queued' | 'completed' | 'error' | 'failed';

export interface ProcessingConfig {
  title: string;
  description?: string;
  processingMessage?: string;
  errorMessage?: string;
  showProgress?: boolean;
  onRetry?: () => void;
  onClose?: () => void;
  onMinimize?: () => void;
}

interface ProcessingOverlayState {
  isVisible: boolean;
  isMinimized: boolean;
  status: ProcessingStatus;
  config: ProcessingConfig | null;
  error: string | null;
  progress: number;
  currentItem: number;
  totalItems: number;
  message: string | null;
}

interface ProcessingOverlayActions {
  show: (config: ProcessingConfig) => void;
  updateStatus: (status: ProcessingStatus, error?: string | null) => void;
  updateProgress: (updates: Partial<{ progress: number; currentItem: number; totalItems: number; message: string }>) => void;
  minimize: () => void;
  close: () => void;
}

interface ProcessingOverlayStore extends ProcessingOverlayState, ProcessingOverlayActions {}

const initialState: ProcessingOverlayState = {
  isVisible: false,
  isMinimized: false,
  status: 'idle',
  config: null,
  error: null,
  progress: 0,
  currentItem: 0,
  totalItems: 0,
  message: null,
};

export const useProcessingOverlayStore = create<ProcessingOverlayStore>((set) => ({
  ...initialState,

  show: (config) => {
    set({
      isVisible: true,
      isMinimized: false,
      status: 'processing',
      config,
      error: null,
      progress: 0,
      currentItem: 0,
      totalItems: 0,
      message: null,
    });
  },

  updateStatus: (status, error = null) => {
    set({ status, error });
  },

  updateProgress: (updates) => {
    set((state) => {
      const progress = updates.progress ?? state.progress;
      const currentItem = updates.currentItem ?? state.currentItem;
      const totalItems = updates.totalItems ?? state.totalItems;
      const message = updates.message ?? state.message;
      if (
        progress === state.progress &&
        currentItem === state.currentItem &&
        totalItems === state.totalItems &&
        message === state.message
      ) {
        return state;
      }
      return { progress, currentItem, totalItems, message };
    });
  },

  minimize: () => {
    set({ isMinimized: true });
  },

  close: () => {
    set(initialState);
  },
}));

export const useProcessingOverlay = () => {
  const show = useProcessingOverlayStore((s) => s.show);
  const updateStatus = useProcessingOverlayStore((s) => s.updateStatus);
  const updateProgress = useProcessingOverlayStore((s) => s.updateProgress);
  const close = useProcessingOverlayStore((s) => s.close);
  return { show, updateStatus, updateProgress, close };
};

export const useGatedLoading = (flag: boolean) => {
  const isMinimized = useProcessingOverlayStore((s) => s.isMinimized);
  return isMinimized ? false : flag;
};

/** Canonical overlay lifecycle runner: show → await fn → completed + delayed
 *  close; on error the overlay stays open in its dismissable error state.
 *  Returns fn's result, or null when fn rejected. */
export async function runWithOverlay<T>(
  config: ProcessingConfig,
  fn: () => Promise<T>,
): Promise<T | null> {
  const { show, updateStatus, close } = useProcessingOverlayStore.getState();
  show(config);
  try {
    const result = await fn();
    updateStatus('completed');
    setTimeout(close, 2000);
    return result;
  } catch (err) {
    updateStatus('error', err instanceof Error ? err.message : null);
    return null;
  }
}
